// SignIn.jsx
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { apiFetchJson } from "../apiClient";
import API_BASE_URL, { API_PREFIX } from "../config";
import "../auth.css";

// Codes the backend appends as `?sso_error=` after a failed IdP round-trip
// (backend/app/api/sso.py). Anything else shows the generic message.
export const SSO_ERROR_CODES = Object.freeze([
  "idp_error", "invalid_request", "invalid_state", "state_unavailable", "invalid_token",
  "provider_error", "provider_unreachable", "provider_misconfigured", "missing_claim",
  "tenant_not_allowed", "domain_not_allowed", "account_exists", "not_provisioned",
  "account_disabled", "workspace_conflict", "too_many_groups", "groups_unavailable",
  "internal_error",
]);
const SSO_ERROR_PARAM = "sso_error";
const FORBIDDEN = 403;

export const ssoErrorKey = (code) =>
  `auth.ssoErrors.${SSO_ERROR_CODES.includes(code) ? code : "generic"}`;

/** Sign-in options as advertised by the backend. If they cannot be loaded the
 * password form is offered anyway — a broken options endpoint must never lock
 * everyone out. status ∈ loading | ready | failed. */
function useSignInOptions() {
  const [state, setState] = useState({ status: "loading", localLogin: null, providers: [] });
  useEffect(() => {
    let cancelled = false;
    apiFetchJson(`${API_PREFIX}/auth/providers`)
      .then((data) => {
        if (cancelled) return;
        setState({
          status: "ready",
          localLogin: (data && data.local_login) || null,
          providers: Array.isArray(data && data.providers) ? data.providers : [],
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "failed", localLogin: null, providers: [] });
      });
    return () => { cancelled = true; };
  }, []);
  return state;
}

export default function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ssoError] = useState(() => searchParams.get(SSO_ERROR_PARAM));
  const options = useSignInOptions();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [redirectingTo, setRedirectingTo] = useState(null);

  // The error is shown once; drop it from the URL so a reload doesn't repeat it.
  useEffect(() => {
    if (!searchParams.has(SSO_ERROR_PARAM)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(SSO_ERROR_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err.status === FORBIDDEN
        ? t("auth.localLoginDisabled")
        : err.message || t("auth.invalidCredentials"));
    } finally {
      setSubmitting(false);
    }
  };

  // SSO is a full-page navigation: the backend redirects to the IdP and back.
  const startSso = (provider) => {
    setRedirectingTo(provider.name);
    window.location.assign(`${API_BASE_URL}${provider.login_url}`);
  };

  const settled = options.status !== "loading";
  const localLogin = options.localLogin;
  const showPasswordForm = settled && (options.status === "failed" || !localLogin || localLogin.enabled !== false);
  const adminOnly = showPasswordForm && Boolean(localLogin && localLogin.admin_only);
  const noMethod = options.status === "ready" && !showPasswordForm && options.providers.length === 0;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2 className="auth-title">{t("auth.welcomeBack")}</h2>
        <p className="auth-subtitle">{t("auth.signInSubtitle")}</p>
        {ssoError && <p className="auth-error" role="alert">{t(ssoErrorKey(ssoError))}</p>}
        {error && <p className="auth-error" role="alert">{error}</p>}
        {!settled && <p className="auth-note">{t("auth.loadingOptions")}</p>}
        {noMethod && <p className="auth-note">{t("auth.noSignInMethod")}</p>}

        {options.providers.length > 0 && (
          <div className="auth-sso">
            {options.providers.map((p) => (
              <button
                key={p.name}
                type="button"
                className="auth-btn auth-btn--sso"
                disabled={redirectingTo !== null}
                onClick={() => startSso(p)}
              >
                {redirectingTo === p.name
                  ? t("auth.redirecting")
                  : t("auth.signInWith", { provider: p.display_name || p.name })}
              </button>
            ))}
          </div>
        )}
        {options.providers.length > 0 && showPasswordForm && (
          <div className="auth-divider" role="separator"><span>{t("auth.or")}</span></div>
        )}

        {showPasswordForm && (
          <>
            {adminOnly && <p className="auth-note">{t("auth.adminOnlyNote")}</p>}
            <form onSubmit={handleSignIn} className="auth-form">
              <input
                type="email"
                placeholder={t("auth.email")}
                aria-label={t("auth.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div className="auth-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("auth.password")}
                  aria-label={t("auth.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              <button type="submit" className="auth-btn" disabled={submitting}>
                {submitting ? t("auth.signingIn") : t("auth.signIn")}
              </button>
            </form>
            <p className="auth-link">
              <Link to="/forgot-password">{t("auth.forgotPassword")}</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
